
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import TableLoadingRow from "../../components/TableLoadingRow";
import { getSalesReportsService } from "../../services/salesService";

type ReportItem = {
    productName: string;
    quantity: number;
    unitPrice: number;
    total: number;
};

type ReportRecord = {
    _id: string;
    createdAt: string;
    total: number;
    items: ReportItem[];
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
};

const toDateText = (value: string): string => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string } } };
    return errObj.response?.data?.message ?? fallback;
};

const SalesReportsPage = () => {
    const { user } = useAuth();
    const [rows, setRows] = useState<ReportRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState<"all" | "day" | "week" | "month" | "custom">("all");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    const fetchReports = useCallback(async () => {
        if (!user?.id) return;

        setLoading(true);
        setError("");

        try {
            const response = await getSalesReportsService();
            const normalized = response.map((entry) => {
                const sale = asRecord(entry);
                const rawItems = Array.isArray(sale.items)
                    ? sale.items
                    : Array.isArray(sale.soldItems)
                        ? sale.soldItems
                        : Array.isArray(sale.products)
                            ? sale.products
                            : [];

                const items = rawItems.map((rawItem) => {
                    const item = asRecord(rawItem);
                    const product = asRecord(item.product);
                    const quantity = asNumber(item.quantity || item.qty || item.count);
                    const unitPrice = asNumber(item.unitPrice || item.price);
                    const total = asNumber(item.total) || quantity * unitPrice;

                    return {
                        productName: asString(item.productName) || asString(product.productName) || asString(product.name) || "-",
                        quantity,
                        unitPrice,
                        total,
                    };
                });

                const computedTotal = items.reduce((sum, item) => sum + item.total, 0);

                return {
                    _id: asString(sale._id) || asString(sale.id) || crypto.randomUUID(),
                    createdAt: asString(sale.createdAt) || asString(sale.date) || asString(sale.checkoutDate),
                    total: asNumber(sale.total) || asNumber(sale.amount) || computedTotal,
                    items,
                };
            });

            setRows(normalized);
        } catch (err: unknown) {
            console.error("Failed to fetch sales reports", err);
            setError(readErrorMessage(err, "Failed to load sales reports"));
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const filteredRows = useMemo(() => {
        if (filter === "all") return rows;

        const now = new Date();
        let start: Date | null = null;
        let end: Date | null = null;

        if (filter === "day") {
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        } else if (filter === "week") {
            const day = now.getDay();
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
            end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 7);
        } else if (filter === "month") {
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        } else if (filter === "custom") {
            if (customStart) start = new Date(customStart);
            if (customEnd) {
                const tmp = new Date(customEnd);
                end = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate() + 1);
            }
        }

        return rows.filter((row) => {
            if (!row.createdAt) return false;
            const date = new Date(row.createdAt);
            if (Number.isNaN(date.getTime())) return false;
            if (start && date < start) return false;
            if (end && date >= end) return false;
            return true;
        });
    }, [rows, filter, customStart, customEnd]);

    const totalSales = filteredRows.length;
    const totalRevenue = filteredRows.reduce((sum, row) => sum + row.total, 0);
    const totalItems = filteredRows.reduce(
        (sum, row) => sum + row.items.reduce((acc, item) => acc + item.quantity, 0),
        0
    );
    const averageTicket = totalSales ? totalRevenue / totalSales : 0;

    return (
        <div className="p-6 w-full">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Sales Reports</h1>
                    <p className="text-sm text-gray-500">Checkout history and performance overview</p>
                </div>

            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Total Sales</p>
                    <p className="text-2xl font-bold mt-1">{totalSales}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Revenue</p>
                    <p className="text-2xl font-bold mt-1">${totalRevenue.toFixed(2)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Items Sold</p>
                    <p className="text-2xl font-bold mt-1">{totalItems}</p>
                </div>

            </div>

            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div className="flex flex-col">
                    <label className="text-sm mb-1">Filter</label>
                    <select
                        value={filter}
                        onChange={(event) => setFilter(event.target.value as "all" | "day" | "week" | "month" | "custom")}
                        className="border p-2 rounded-md"
                    >
                        <option value="all">All</option>
                        <option value="day">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="custom">Custom</option>
                    </select>
                </div>

                {filter === "custom" && (
                    <>
                        <div className="flex flex-col">
                            <label className="text-sm mb-1">Start</label>
                            <input
                                type="date"
                                value={customStart}
                                onChange={(event) => setCustomStart(event.target.value)}
                                className="border p-2 rounded-md"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm mb-1">End</label>
                            <input
                                type="date"
                                value={customEnd}
                                onChange={(event) => setCustomEnd(event.target.value)}
                                className="border p-2 rounded-md"
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="w-full min-w-[780px]">
                    <thead className="bg-black text-white">
                        <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Items</th>
                            <th className="text-left p-3">Item Details</th>
                            <th className="text-left p-3">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <TableLoadingRow colSpan={4} label="Loading sales reports..." />
                        ) : filteredRows.map((row) => {
                            const count = row.items.reduce((sum, item) => sum + item.quantity, 0);
                            return (
                                <tr key={row._id} className="border-t border-gray-100 align-top">
                                    <td className="p-3">{toDateText(row.createdAt)}</td>
                                    <td className="p-3 font-medium">{count}</td>
                                    <td className="p-3">
                                        {row.items.length ? (
                                            <div className="space-y-1 text-sm">
                                                {row.items.map((item, idx) => (
                                                    <p key={`${row._id}-${idx}`}>
                                                        {item.productName} x{item.quantity} (${item.unitPrice.toFixed(2)})
                                                    </p>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">No items</span>
                                        )}
                                    </td>
                                    <td className="p-3 font-semibold">${row.total.toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {!loading && filteredRows.length === 0 && (
                    <div className="p-6 text-center text-gray-500">No sales records found</div>
                )}
            </div>
        </div>
    );
};

export default SalesReportsPage;
